import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaBullseye, FaPlus, FaTrash, FaSpinner } from 'react-icons/fa';
import { useCurrency } from '../context/CurrencyContext';

const GoalsWidget = ({ totalPortfolioValue }) => {
    const { formatPrice } = useCurrency();
    const [goals, setGoals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);

    // Form State
    const [title, setTitle] = useState('');
    const [targetAmount, setTargetAmount] = useState('');
    const [currency, setCurrency] = useState('TRY');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        fetchGoals();
    }, []);

    const fetchGoals = async () => {
        try {
            const { data } = await axios.get('/api/goals');
            setGoals(data);
            setLoading(false);
        } catch (error) {
            console.error('Hedefler yüklenemedi:', error);
            setLoading(false);
        }
    };

    const handleAddGoal = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await axios.post('/api/goals', {
                title,
                targetAmount: parseFloat(targetAmount),
                currency
            });
            await fetchGoals();
            setShowAddModal(false);
            // Reset form
            setTitle('');
            setTargetAmount('');
            setCurrency('TRY');
        } catch (error) {
            console.error('Goal creation error:', error);
            alert('Hedef oluşturulamadı');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Bu hedefi silmek istediğinize emin misiniz?')) return;
        try {
            await axios.delete(`/api/goals/${id}`);
            fetchGoals();
        } catch (error) {
            console.error('Goal deletion error:', error);
        }
    };

    const calculateProgress = (goal) => {
        if (!totalPortfolioValue) return 0;

        let currentVal = totalPortfolioValue;

        // Basitlik için, eğer hedef döviz cinsindense güncel portföy değerini o dövize çevirmek lazım
        // Şimdilik varsayım: totalPortfolioValue TRY geliyor.
        // Eğer hedef TRY ise direkt böl.
        // Eğer hedef USD/EUR ise, hedef tutarı TRY'ye çevirmek daha güvenli.

        // Ancak convertPrice fonksiyonumuz (ValueInTry -> DisplayCurrency) yönünde çalışıyor.
        // Bizim ihtiyacımız: HedefParaBirimi -> TRY veya TRY -> HedefParaBirimi

        // Şöyle yapalım: Hedef ne olursa olsun, gösterilen progress TRY bazlı olsun (ya da kullanıcının seçtiği displayCurrency).
        // Ama kullanıcı "1000 USD" hedeflediyse, 1000 USD kaç TL ediyor onu bulup oranlayacağız.

        // MVP için: Şimdilik sadece TRY hedeflerini destekleyelim veya backend'den kur dönüştürüp kaydedelim.
        // Daha da basiti: Kullanıcı TRY hedefi girsin.
        return Math.min(100, (currentVal / goal.targetAmount) * 100);
    };

    if (loading) return (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 h-full flex items-center justify-center">
            <FaSpinner className="animate-spin text-2xl text-blue-600" />
        </div>
    );

    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 h-full border border-gray-200 dark:border-gray-700 flex flex-col">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                    <FaBullseye className="text-red-500" />
                    Hedeflerim
                </h3>
                <button
                    onClick={() => setShowAddModal(true)}
                    className="p-1.5 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-800 transition"
                >
                    <FaPlus size={12} />
                </button>
            </div>

            <div className="space-y-4 overflow-y-auto max-h-[300px] custom-scrollbar flex-1">
                {goals.length === 0 ? (
                    <div className="text-center text-gray-500 dark:text-gray-400 py-4 italic text-sm">
                        Henüz bir hedef belirlemediniz.
                        <br />
                        <span className="text-xs">"Araba", "Ev" gibi hedefler ekleyin.</span>
                    </div>
                ) : (
                    goals.map(goal => {
                        const progress = calculateProgress(goal);
                        // Eğer totalPortfolioValue yoksa 0 (Home'dan gelmeli)

                        return (
                            <div key={goal._id} className="relative group">
                                <div className="flex justify-between items-end mb-1">
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{goal.title}</span>
                                    <span className="text-xs font-bold text-gray-900 dark:text-white">
                                        %{progress.toFixed(1)}
                                    </span>
                                </div>
                                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                                    <div
                                        className={`h-2.5 rounded-full transition-all duration-1000 ${progress >= 100 ? 'bg-green-500' : 'bg-blue-600'}`}
                                        style={{ width: `${progress}%` }}
                                    ></div>
                                </div>
                                <div className="flex justify-between mt-1 text-xs text-gray-500 dark:text-gray-400">
                                    <span>{formatPrice(totalPortfolioValue)}</span>
                                    <span>{new Intl.NumberFormat('tr-TR', { style: 'currency', currency: goal.currency }).format(goal.targetAmount)}</span>
                                </div>

                                <button
                                    onClick={() => handleDelete(goal._id)}
                                    className="absolute -right-2 -top-2 bg-red-100 dark:bg-red-900 text-red-500 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                    <FaTrash size={10} />
                                </button>
                            </div>
                        );
                    })
                )}
            </div>

            {/* Add Modal */}
            {showAddModal && (
                <div className="absolute inset-0 z-50 flex items-center justify-center p-4 bg-black/50 rounded-lg">
                    <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-lg w-full max-w-xs animate-fadeIn">
                        <h4 className="text-md font-bold mb-3 text-gray-900 dark:text-white">Yeni Hedef</h4>
                        <form onSubmit={handleAddGoal} className="space-y-3">
                            <input
                                type="text"
                                placeholder="Hedef Adı (Örn: Araba)"
                                className="w-full p-2 text-sm border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                value={title}
                                onChange={e => setTitle(e.target.value)}
                                required
                            />
                            <div className="flex gap-2">
                                <input
                                    type="number"
                                    placeholder="Tutar"
                                    className="w-full p-2 text-sm border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                    value={targetAmount}
                                    onChange={e => setTargetAmount(e.target.value)}
                                    required
                                />
                                <select
                                    className="p-2 text-sm border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                    value={currency}
                                    onChange={e => setCurrency(e.target.value)}
                                >
                                    <option value="TRY">TRY</option>
                                    {/* <option value="USD">USD</option>
                                    <option value="EUR">EUR</option> */}
                                </select>
                            </div>
                            <div className="flex gap-2 pt-2">
                                <button type="button" onClick={() => setShowAddModal(false)} className="flex-1 px-3 py-1.5 text-sm bg-gray-200 dark:bg-gray-700 rounded text-gray-700 dark:text-gray-300">İptal</button>
                                <button type="submit" disabled={submitting} className="flex-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded">Kaydet</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GoalsWidget;
